import { Alert, Box, Button, Collapse, Divider, Link, Modal, Text } from '@ksyos/design-system';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEpisodeCategory } from '@common/interfaces';
import type { ViewMedicalDossierPatientConsent } from '@common/interfaces/viewMedicalDossierPatientConsent';
import type { IReduxState } from '@frontend/configureStore';
import { ServiceCode, ToastType } from '@common/enums';
import { viewMedicalDossierPatientConsentAddSchema } from '@common/validation/viewMedicalDossierPatientConsent';
import { translateKey } from '@frontend/translate';
import { showToast, useAbortableFetch, validate } from '@frontend/utils';
import {
    consentDeclarationExplainer,
    consentDeclarationParagraph1,
    consentDeclarationParagraph2Sub1,
    consentDeclarationParagraph2Sub2,
    consentDeclarationParagraph3,
    consentDeclarationParagraph4,
    consentDeclarationSubtitle1,
    consentDeclarationSubtitle2,
    consentDeclarationSubtitle3,
    consentDeclarationSubtitle4,
} from './consentDeclarationTexts';

interface IViewMedicalDossierPatientConsentModalProps {
    isOpen: boolean;
    onCancel: () => void;
    onSaved: () => void;
    patientId: string;
}

interface IService {
    code: ServiceCode;
    episodeCategoryId: string;
}

type SelectableEpisodeCategory = { isSelected: boolean } & IEpisodeCategory;

export function ViewMedicalDossierPatientConsentModal(props: IViewMedicalDossierPatientConsentModalProps) {
    const abortableFetch = useAbortableFetch();

    const [isFullTextVisible, setIsFullTextVisible] = useState(false);
    const [episodeCategories, setEpisodeCategories] = useState<SelectableEpisodeCategory[]>([]);
    const [isConsentWithheld, setIsConsentWithheld] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    useEffect(() => {
        if (props.isOpen) {
            loadAndSetEpisodeCategories();
        } else {
            setIsFullTextVisible(false);
            setIsValid(true);
        }
    }, [props.isOpen]);

    if (episodeCategories.length === 0) {
        return null;
    }

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onCancel}
            size="medium"
        >
            <Modal.Header>{translateKey('exchangePatientData.consentRequiredModalTitle')}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <Text mt={3}>{consentDeclarationExplainer}</Text>
                <Collapse isOpen={isFullTextVisible}>
                    <Box mt={6}>
                        <Text
                            fontWeight="bold"
                            mb={2}
                            size="md"
                        >
                            {consentDeclarationSubtitle1}
                        </Text>
                        <Text
                            mb={6}
                            size="md"
                        >
                            {consentDeclarationParagraph1}
                        </Text>
                        <Text
                            fontWeight="bold"
                            mb={2}
                            size="md"
                        >
                            {consentDeclarationSubtitle2}
                        </Text>
                        <Text
                            mb={6}
                            size="md"
                        >
                            {consentDeclarationParagraph2Sub1}
                        </Text>
                        <Text
                            mb={6}
                            size="md"
                        >
                            {consentDeclarationParagraph2Sub2}
                        </Text>
                        <Text
                            fontWeight="bold"
                            mb={2}
                            size="md"
                        >
                            {consentDeclarationSubtitle3}
                        </Text>
                        <Text
                            mb={6}
                            size="md"
                        >
                            {consentDeclarationParagraph3}
                        </Text>
                        <Text
                            fontWeight="bold"
                            mb={2}
                            size="md"
                        >
                            {consentDeclarationSubtitle4}
                        </Text>
                        <Text size="md">{consentDeclarationParagraph4}</Text>
                    </Box>
                </Collapse>
                <Link
                    color="subtle"
                    fontWeight="normal"
                    iconLeft={isFullTextVisible ? 'caret-up' : 'caret-down'}
                    onClick={() => setIsFullTextVisible(!isFullTextVisible)}
                    sx={{ fontSize: '14px', mb: 6, mt: 3 }}
                    variant="button"
                >
                    {isFullTextVisible
                        ? translateKey('exchangePatientData.consentReadLess')
                        : translateKey('exchangePatientData.consentReadMore')}
                </Link>
                <Alert
                    appearance={isValid ? 'info' : 'warning'}
                    noMargin={true}
                >
                    {isValid
                        ? translateKey('exchangePatientData.consentDeclarationTitle')
                        : translateKey('exchangePatientData.consentDeclarationTitleWarning')}

                    <Box>
                        <Box
                            p={2}
                            sx={{ mt: 6, backgroundColor: isValid ? '#0087C121' : '#D3750221' }}
                        >
                            <Alert.Checkbox
                                checked={areAllChecked()}
                                label={
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                        size="sm"
                                        sx={{ color: getTextColor() }}
                                    >
                                        {translateKey('exchangePatientData.selectAllServices')}
                                    </Text>
                                }
                                onChange={handleChangeConsentToAll}
                            />
                        </Box>
                        <Box
                            pt={2}
                            px={2}
                        >
                            <Box sx={{ columnCount: [null, 2] }}>
                                {episodeCategories.map((episodeCategory) => {
                                    return (
                                        <Alert.Checkbox
                                            checked={episodeCategory.isSelected}
                                            key={episodeCategory.id}
                                            label={
                                                <Text
                                                    as="span"
                                                    size="sm"
                                                    sx={{ color: getTextColor() }}
                                                >
                                                    {translateKey(`episodeCategoryEnum.${episodeCategory.name}`)}
                                                </Text>
                                            }
                                            onChange={() => handleChangeEpisodeCategories(episodeCategory.id)}
                                            sx={{ mb: 2 }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        <Divider spacing="small" />
                        <Box px={2}>
                            <Alert.Checkbox
                                checked={isConsentWithheld}
                                label={
                                    <Text
                                        as="span"
                                        size="sm"
                                        sx={{ color: getTextColor() }}
                                    >
                                        {translateKey('exchangePatientData.thePatientGives')}{' '}
                                        <Text
                                            as="span"
                                            fontWeight="bold"
                                            size="sm"
                                            sx={{ color: getTextColor() }}
                                        >
                                            {translateKey('exchangePatientData.noConsent')}
                                        </Text>{' '}
                                        {translateKey('exchangePatientData.aboveServices')}
                                    </Text>
                                }
                                onChange={handleChangeNoConsent}
                            />
                        </Box>
                    </Box>
                </Alert>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={props.onCancel}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>
                <Button onClick={async () => await handleSubmit()}>{translateKey('common.save')}</Button>
            </Modal.Footer>
        </Modal>
    );

    function getTextColor() {
        return isValid ? 'lightBlue700' : 'orange700';
    }

    function isNoDataEntered() {
        if (isConsentWithheld) {
            return false;
        }

        if (episodeCategories.some((episodeCategory) => episodeCategory.isSelected)) {
            return false;
        }

        return true;
    }

    function handleChangeEpisodeCategories(episodeCategoryId: string) {
        setIsConsentWithheld(false);
        setIsValid(true);
        setEpisodeCategories(
            episodeCategories.map((episodeCategory: SelectableEpisodeCategory) => {
                if (episodeCategory.id === episodeCategoryId) {
                    return {
                        ...episodeCategory,
                        isSelected: !episodeCategory.isSelected,
                    };
                }

                return episodeCategory;
            })
        );
    }

    function handleChangeConsentToAll(event: React.ChangeEvent<HTMLInputElement>) {
        setIsConsentWithheld(false);
        setIsValid(true);
        setEpisodeCategories(
            episodeCategories.map((episodeCategory: SelectableEpisodeCategory) => ({
                ...episodeCategory,
                isSelected: event.target.checked,
            }))
        );
    }

    function handleChangeNoConsent() {
        setIsConsentWithheld((current) => !current);
        setIsValid(true);
        setEpisodeCategories(
            episodeCategories.map((episodeCategory: SelectableEpisodeCategory) => ({
                ...episodeCategory,
                isSelected: false,
            }))
        );
    }

    function areAllChecked() {
        const numberOfEpisodeCategories = episodeCategories.length;

        return (
            numberOfEpisodeCategories > 0 &&
            episodeCategories.filter((episodeCategory) => episodeCategory.isSelected).length ===
                numberOfEpisodeCategories
        );
    }

    async function handleSubmit() {
        const noDataEntered = isNoDataEntered();

        if (noDataEntered) {
            setIsValid(false);

            return;
        }

        const url = '/api/viewMedicalDossierPatientConsent';

        const selectedEpisodeCategories = episodeCategories
            .filter((episodeCategory) => episodeCategory.isSelected)
            .map((episodeCategory) => episodeCategory.id);
        const { hasErrors, item: body } = validate(viewMedicalDossierPatientConsentAddSchema, {
            patientId: props.patientId,
            episodeCategoryIds: selectedEpisodeCategories,
        });

        if (hasErrors || !body) {
            return;
        }

        await abortableFetch.postJson(url, body);

        showToast(ToastType.success, translateKey('toast.consentSaved'));

        props.onSaved();
    }

    async function loadAndSetEpisodeCategories() {
        const currentConsent = await loadCurrentConsent();
        const usedEpisodeCategories = await loadEpisodeCategoriesWithServicesOtherThanPilot(currentConsent);

        setEpisodeCategories(usedEpisodeCategories);
        setIsConsentWithheld(
            Boolean(!isEmpty(currentConsent) && (currentConsent?.episodeCategories ?? []).length === 0)
        );
    }

    async function loadEpisodeCategoriesWithServicesOtherThanPilot(
        currentConsent: ViewMedicalDossierPatientConsent | undefined
    ) {
        const servicesWithoutPilotServices: IService[] = (
            await abortableFetch.getJson('/api/services?selectionList=true')
        ).json.items.filter((item: IService) => item.code !== ServiceCode.PILOT);
        const allEpisodeCategories = (await abortableFetch.getJson('/api/episodeCategories?isActive=true')).json.items;

        return allEpisodeCategories.reduce(
            (filtered: SelectableEpisodeCategory[], episodeCategory: IEpisodeCategory) => {
                const episodeCategoryServices = servicesWithoutPilotServices.filter(
                    (service) => service.episodeCategoryId === episodeCategory.id
                );

                if (episodeCategoryServices.length > 0) {
                    const updatedItem = {
                        ...episodeCategory,
                        isSelected: Boolean(
                            (currentConsent?.episodeCategories ?? []).some(
                                (category) => category.id === episodeCategory.id
                            )
                        ),
                    };
                    filtered.push(updatedItem);
                }

                return filtered;
            },
            []
        );
    }

    async function loadCurrentConsent() {
        const consentResponse = await abortableFetch.getJson(
            `/api/viewMedicalDossierPatientConsent?organizationId=${organizationId}&patientId=${props.patientId}`
        );

        return consentResponse.json as ViewMedicalDossierPatientConsent | undefined;
    }
}
